import React from 'react';
import { Chip } from '@mui/material';
import {
  HourglassEmpty as PendingIcon,
  Sync as ProcessingIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  Info as DuplicateIcon,
  Warning as ReviewIcon,
} from '@mui/icons-material';
import { InvoiceImportStatus } from '../Types/invoiceImport.types';

interface ImportStatusBadgeProps {
  status: InvoiceImportStatus;
  statusLabel?: string;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
}

/**
 * Status badge configuration
 */
const STATUS_CONFIG: Record<
  InvoiceImportStatus,
  {
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    icon: React.ReactElement;
    defaultLabel: string;
  }
> = {
  [InvoiceImportStatus.Pending]: {
    color: 'info',
    icon: <PendingIcon />,
    defaultLabel: 'Oczekuje',
  },
  [InvoiceImportStatus.Processing]: {
    color: 'warning',
    icon: <ProcessingIcon />,
    defaultLabel: 'Przetwarzanie',
  },
  [InvoiceImportStatus.Completed]: {
    color: 'success',
    icon: <CompletedIcon />,
    defaultLabel: 'Zakończono',
  },
  [InvoiceImportStatus.Failed]: {
    color: 'error',
    icon: <FailedIcon />,
    defaultLabel: 'Błąd',
  },
  [InvoiceImportStatus.Duplicate]: {
    color: 'warning',
    icon: <DuplicateIcon />,
    defaultLabel: 'Duplikat',
  },
  [InvoiceImportStatus.RequiresReview]: {
    color: 'warning',
    icon: <ReviewIcon />,
    defaultLabel: 'Wymaga weryfikacji',
  },
  [InvoiceImportStatus.Rejected]: {
    color: 'error',
    icon: <FailedIcon />,
    defaultLabel: 'Odrzucony',
  },
  [InvoiceImportStatus.Abandoned]: {
    color: 'default',
    icon: <ReviewIcon />,
    defaultLabel: 'Porzucony',
  },
};

/**
 * Status badge component for invoice import
 * Displays colored chip with icon based on import status
 */
const ImportStatusBadge: React.FC<ImportStatusBadgeProps> = ({
  status,
  statusLabel,
  size = 'medium',
  variant = 'filled',
}) => {
  const config = STATUS_CONFIG[status];

  if (!config) {
    // Fallback for unknown status
    return <Chip label={statusLabel || 'Nieznany'} size={size} variant={variant} color="default" />;
  }

  const label = statusLabel || config.defaultLabel;

  return (
    <Chip
      label={label}
      size={size}
      variant={variant}
      color={config.color}
      icon={config.icon}
      sx={{
        fontWeight: 'medium',
        // Animate Processing status
        ...(status === InvoiceImportStatus.Processing && {
          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0.7,
            },
          },
        }),
      }}
    />
  );
};

export default ImportStatusBadge;
